/**
 * The session page's teaching boxes after Prepare, in the order class needs
 * them (A2 session composition): the Running order, this session's
 * Coursework, then the private Teaching notes and the optional Session folder
 * as closed disclosures whose faces say what is unfinished.
 *
 * Coursework is not edited here. Each row visits the canonical author or
 * review (`work:`), and Add coursework visits the canonical Create, which
 * starts placed in this session's plan; the author publishes with its own
 * key and expected version (ADR 0040).
 */
import { useContext } from "react";
import { useLocation } from "react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import { a3Copy } from "@/components/assignments/a3-copy";
import { teachingDraftsKey } from "@/components/assignments/coursework/teaching-draft-model";

import { DriveContainerAffordance } from "@/components/courses/DriveContainerAffordance";
import { useDriveContainerSummary } from "@/components/courses/drive-container-summary";
import type { BoxState, FactBox, ListBox, Read } from "@/components/ui/box.schema";
import { BentoVisit } from "@/components/ui/box-visit-context";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { formatInCourseZone } from "@/lib/session-portal";
import type { SessionWorkspace, TeachingSessionAssignment } from "@/lib/api/sessionPortal";
import { teachingDraftsApi, type TeachingDraftHeader } from "@/lib/api/teachingDrafts";

import type { SessionPlanParts } from "../ClassPlanCard";
import { PlanTaskSaveStatus, TeachingNotesField } from "../SessionPlanFields";
import { RunningOrderBody } from "../TeachingView";
import { reviewHash } from "./use-session-review-boxes";
import { useMaterialLibrary } from "@/components/courses/materials/use-material-library";
import { SESSION_COPY } from "./session-copy";
import { useUnitProjection } from "./use-unit-projection";

const at = () => new Date().toISOString();

/** The teaching team's running order, composed once, usable in class. Absent with no plan. */
export function useRunningOrderBox({ workspace, parts }: { workspace: SessionWorkspace; parts: SessionPlanParts }): FactBox<null> | null {
  const copy = SESSION_COPY.runningOrder;
  const host = useContext(BentoVisit);
  const { course, session, viewer } = workspace;
  const library = useMaterialLibrary(course.id);
  const projected = useUnitProjection(course.id, parts.unit);
  if (!projected || !parts.outline) return null;
  const unit = projected;
  const placed = new Map(library.items.filter(item => item.placement).map(item => [item.placement!.id, item]));
  // A referenced reading opens its reading; anything else its material (as Outline's resources do).
  const resourceAddress = (placementId: string) => {
    const item = placed.get(placementId);
    const reading = item ? item.material.reading_id ?? item.placement?.reading_id : null;
    return reading ? `reading:${course.id}/${reading}` as const : `material:${course.id}/${placementId}` as const;
  };
  const createAllowed = Boolean(host) && course.term_open && viewer.allowed_actions.includes("manage_session_coursework");
  const count = parts.state?.draft.activities.length ?? unit.activities?.length ?? 0;
  // Before and during class it stays open; a held or cancelled session's order is history.
  const past = workspace.session.status === "held" || workspace.session.status === "cancelled";
  return {
    kind: "fact", id: "running-order", label: copy.label, job: copy.job, owner: { system: "imam" },
    read: { status: "ready", value: null, readAt: at() },
    fold: past ? "choice" : "never",
    act: past ? { does: "open", verb: copy.open } : undefined,
    detail: () => copy.detail(count),
    body: () => <RunningOrderBody sessionId={session.id} courseId={course.id} timezone={course.timezone} unit={unit} activities={parts.state?.draft.activities}
      editable={Boolean(parts.state && !parts.state.refused)} createAllowed={createAllowed} resourceAddress={resourceAddress}
      outline={parts.outline} saveStatus={parts.saveField === "activities" && parts.state && <PlanTaskSaveStatus state={parts.state} />} />,
  };
}

type Row = TeachingSessionAssignment;
/** A piece of this session's work, or private writing placed in its plan that is not coursework yet. */
type Entry = { kind: "work"; id: string; work: Row } | { kind: "draft"; id: string; draft: TeachingDraftHeader };

/** Work with nothing to hand in is marked done or taken; in-class work is recorded by the teacher, so nothing arrives. */
function arrived(row: Row): string | null {
  const { submitted_count: done, enrolled_count: of } = row.coverage;
  if (row.assessment_kind === "in_class") return null;
  if (row.assessment_kind === "external_quiz") return a3Copy.markedAsTakenCount(done, of);
  const nothing = row.expected_deliverable === null || (row.expected_deliverable as string) === "none";
  return (row.assessment_kind === "completion" && nothing ? a3Copy.markedDoneCount : a3Copy.handInCount)(done, of);
}

/** This session's coursework, read from the session workspace, worked in the canonical workspace. */
export function useSessionCourseworkBox({ workspace, reviewHere }: {
  workspace: SessionWorkspace;
  /** The work whose Review box is on this page: its Review opens that box, never a second one in a visit. */
  reviewHere?: ReadonlySet<string>;
}): ListBox<Entry[], Entry> {
  const copy = SESSION_COPY.coursework;
  const location = useLocation();
  const client = useQueryClient();
  const host = useContext(BentoVisit);
  const { me } = useAuth();
  const { course, viewer } = workspace;
  const unit = workspace.plan.unit;
  const rows = (unit?.assignments ?? []).filter((item): item is Row => "coverage" in item);
  const editable = course.term_open && viewer.allowed_actions.includes("manage_session_coursework");
  // Saved private writing placed in this plan is the session's next step too, though not coursework yet.
  const drafts = useQuery({
    queryKey: teachingDraftsKey(course.id, `${me?.tenant_id}:${me?.user_id}`, 0),
    queryFn: () => teachingDraftsApi.list(course.id, 0), enabled: editable && Boolean(unit && host), retry: false,
  });
  const writing = (drafts.data?.items ?? []).filter(item => item.state === "active" && item.assignment_id === null && unit && item.unit_id === unit.id);
  const when = (value: string) => formatInCourseZone(value, course.timezone, { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
  const entries: Entry[] = [...rows.map(work => ({ kind: "work" as const, id: work.id, work })), ...writing.map(draft => ({ kind: "draft" as const, id: draft.id, draft }))];
  const read: Read<Entry[]> = workspace.plan.coursework_state === "unavailable"
    ? { status: "failed", sentence: copy.unread, retry: () => void client.invalidateQueries({ queryKey: ["session-workspace"] }) }
    : !unit ? { status: "empty", sentence: copy.noPlan }
    // Ready with no rows still draws its tools, so the session can add its first work.
    : { status: "ready", value: entries, readAt: at() };
  const unpublished = rows.filter(row => row.status === "draft").length + writing.length;
  const waiting = rows.reduce((sum, row) => sum + (row.status === "draft" ? 0 : row.coverage.awaiting_review_count), 0);
  // The next deadline still ahead; a passed one is history, not what comes next.
  const now = Date.now();
  const nextDue = rows.filter(row => row.status === "published" && row.due_at && Date.parse(row.due_at) > now).map(row => row.due_at!).sort()[0];
  const state = (): BoxState => waiting ? { class: "waiting", word: copy.toReview(waiting) }
    : unpublished ? { class: "draft", word: copy.notPublished(unpublished) }
    // At rest the list itself counts the work; the state names what comes next.
    : { class: "quiet", word: nextDue ? copy.nextDue(when(nextDue)) : copy.count(rows.length) };
  return {
    kind: "list", id: "session-coursework", label: copy.label, job: copy.job, owner: { system: "imam" }, read,
    fold: "never",
    detail: () => "",
    openDetail: () => course.term_open ? undefined : copy.termClosed,
    // Add coursework visits the canonical Create, placed in this session's plan.
    // Without a plan there is nothing to place it in, so the read is empty and offers none.
    renderTools: value => <div className="flex flex-wrap items-center justify-between gap-3">
      {value.length === 0 && <p className="text-meta text-muted-foreground">{copy.none}</p>}
      {editable && host && <Button type="button" variant="subtle" size="sm" className="ms-auto" data-act="visit" data-visit-to={`work:${course.id}/create`} onClick={() => host.visit(`work:${course.id}/create`, { box: "session-coursework" })}>{copy.add}</Button>}
    </div>,
    state,
    rows: value => value,
    row: {
      key: entry => entry.id,
      title: entry => entry.kind === "draft" ? entry.draft.title || copy.untitled : entry.work.title,
      facts: entry => {
        if (entry.kind === "draft") return copy.privateFacts;
        const row = entry.work;
        return [
          row.due_at ? copy.due(when(row.due_at)) : copy.noDue,
          // Unpublished work has no audience yet, so no arrival figure applies.
          row.status === "draft" ? null : arrived(row),
          row.status === "closed" ? copy.closed : null,
        ].filter(Boolean).join(" · ");
      },
      state: entry => {
        if (entry.kind === "draft") return { class: "draft", word: copy.privateDraft };
        const row = entry.work;
        return row.status === "draft" ? { class: "draft", word: copy.draft }
          : row.coverage.awaiting_review_count ? { class: "waiting", word: copy.toReview(row.coverage.awaiting_review_count) } : null;
      },
      act: entry => {
        if (entry.kind === "draft") return { does: "visit", verb: copy.continue, visit: `teaching-draft:${course.id}/${entry.draft.id}` };
        const row = entry.work;
        if (row.status !== "draft" && row.coverage.awaiting_review_count && reviewHere?.has(row.id))
          return { does: "exit", verb: copy.review, to: `${location.pathname}${location.search}${reviewHash(row.id)}` };
        return row.status !== "draft" && row.coverage.awaiting_review_count
          ? { does: "visit", verb: copy.review, visit: `work:${row.id}/review` }
          : { does: "visit", verb: row.status === "draft" && editable ? copy.continue : copy.open, visit: `work:${row.id}` };
      },
    },
  };
}

/** The teaching team's private notes, closed until opened; the face says whether any are written or unsaved. */
export function useTeachingNotesBox({ workspace, parts }: { workspace: SessionWorkspace; parts: SessionPlanParts }): FactBox<null> | null {
  const copy = SESSION_COPY.notes;
  if (!parts.unit || parts.unavailable) return null;
  const notes = parts.state?.draft.notes ?? parts.unit.teaching_notes ?? "";
  // A tab draft survives reload, so unsaved is the draft against the acknowledged plan, not the field last edited.
  const unsaved = !!parts.state?.dirty && notes !== (parts.unit.teaching_notes ?? "");
  return {
    kind: "fact", id: "teaching-notes", label: copy.label, job: copy.job, owner: { system: "imam" },
    read: { status: "ready", value: null, readAt: at() },
    fold: "choice",
    act: { does: "open", verb: copy.open },
    detail: () => unsaved ? copy.unsaved : notes.trim() ? copy.some : copy.empty,
    body: () => <TeachingNotesField unit={parts.unit} state={parts.state} sessionId={workspace.session.id} onEdit={parts.onEditField} showSaveStatus={parts.saveField === "notes"} />,
  };
}

/** The optional Drive folder, closed until opened; its warning stays at its own action. */
export function useSessionFolderBox({ workspace }: { workspace: SessionWorkspace }): FactBox<null> | null {
  const copy = SESSION_COPY.folder;
  const { course, session, viewer } = workspace;
  const editable = course.term_open && viewer.allowed_actions.includes("manage_session_coursework");
  const scope = { kind: "session" as const, courseId: course.id, sessionId: session.id };
  const read = useDriveContainerSummary(scope, editable);
  if (!read) return null;
  const { summary, retry } = read;
  return {
    kind: "fact", id: "session-folder", label: copy.label, job: copy.job, owner: { system: "imam" },
    read: summary === "failed" ? { status: "failed", sentence: copy.unread, retry } : { status: "ready", value: null, readAt: at() },
    fold: "choice",
    act: { does: "open", verb: copy.open },
    detail: () => ({ ready: copy.ready, pending: copy.pending, absent: copy.absent, unknown: copy.unknown, "needs-course": copy.needsCourse, failed: copy.unread })[summary],
    body: () => <DriveContainerAffordance scope={scope} canEdit={editable} />,
  };
}
